// File Path: monorepo/web/workery-frontend/src/pages/Admin/TaskItem/Update/AssignAssociate/Step2Page.jsx
// UIX Upgraded - Uses UIX primitives (Spinner, Breadcrumb, UIXThemeProvider)

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, Navigate, useParams } from "react-router";
import { useTaskManager } from "../../../../../services/Services";
import { Spinner, Breadcrumb, UIXThemeProvider, useUIXTheme } from "../../../../../components/UIX";
import { CLIENT_PHONE_TYPE_OF_MAP } from "../../../../../constants/FieldOptions";
import { STORAGE_KEYS } from "../../../../../constants/Storage";
import {
  ChevronRightIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  InformationCircleIcon,
  ExclamationCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  TagIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  WrenchScrewdriverIcon,
  BriefcaseIcon,
  UserIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  PauseCircleIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

function AdminTaskItemAssignAssociateStep2Page() {
  // URL Parameters
  const { tid } = useParams();

  // Services
  const taskManager = useTaskManager();

  // UIX Theme
  const { theme } = useUIXTheme();
  const themeClasses = useMemo(() => ({
    container: theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900',
    card: theme === 'dark' ? 'bg-gray-800' : 'bg-white',
    text: theme === 'dark' ? 'text-gray-100' : 'text-gray-900',
    textMuted: theme === 'dark' ? 'text-gray-400' : 'text-gray-600',
  }), [theme]);

  // Memoized breadcrumb items
  const breadcrumbItems = useMemo(() => [
    { label: "Dashboard", path: "/admin/dashboard", icon: ChartBarIcon },
    { label: "Tasks", path: "/admin/tasks", icon: ClipboardDocumentListIcon },
    { label: "Select Associate", icon: UserGroupIcon },
  ], []);

  // Component states
  const [task, setTask] = useState(null);
  const [associates, setAssociates] = useState(null);
  const [errors, setErrors] = useState({});
  const [isFetching, setFetching] = useState(false);
  const [forceURL, setForceURL] = useState("");
  const [selectedAssociateId, setSelectedAssociateId] = useState(null);

  // Event handling
  const onUnauthorized = useCallback(() => {
    setForceURL("/login?unauthorized=true");
  }, []);

  const onSelectClick = (associate) => {
    // Prevent selection if associate is away
    if (associate.isAway) {
      // Do nothing - selection is restricted
      return;
    }

    sessionStorage.setItem(
      STORAGE_KEYS.WORKERY_ASSIGN_ASSOCIATE_DATA,
      JSON.stringify({
        associateID: associate.id,
        associateName: associate.name,
        associatePhone: associate.phone,
        associateEmail: associate.email,
        associateOrganizationName: associate.organizationName,
        associateContactsLast30Days: associate.contactsLast30Days,
        associateWsibNumber: associate.wsibNumber,
        associateHourlySalaryDesired: associate.hourlySalaryDesired,
        associateSkillSets: associate.skillSets,
        associateIsAway: associate.isAway,
      }),
    );
    setForceURL(`/admin/task/${tid}/assign-associate/step-3`);
  };

  // Load previously selected associate from session storage
  useEffect(() => {
    const storedData = sessionStorage.getItem(
      STORAGE_KEYS.WORKERY_ASSIGN_ASSOCIATE_DATA,
    );
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        if (data.associateID) {
          setSelectedAssociateId(data.associateID);
          console.log("Found previously selected associate:", data.associateID);
        }
      } catch (error) {
        console.error("Error parsing stored associate data:", error);
      }
    }
  }, []);

  // Load task details and then load assignable associates
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      if (!mounted) return;

      setFetching(true);
      setErrors({});

      try {
        // First, get the task details
        const taskData = await taskManager.getTaskDetail(tid, onUnauthorized);
        if (!mounted) return;

        setTask(taskData);

        console.log("Fetching assignable associates for task:", tid);

        // Then get the assignable associates for this task
        // The backend will automatically filter by skill sets and sort by contacts
        const associatesData = await taskManager.getAssignableAssociates(
          tid,
          onUnauthorized,
          true, // Force refresh to get latest data
        );

        if (mounted) {
          // The results are already sorted by contactsLast30Days (ascending) from the backend
          // Associates with matching skill sets are included and sorted properly
          setAssociates(associatesData);

          console.log("Assignable associates loaded:", {
            count: associatesData.results ? associatesData.results.length : 0,
            awayCount: associatesData.results
              ? associatesData.results.filter((a) => a.isAway).length
              : 0,
          });
        }
      } catch (error) {
        if (mounted) {
          console.error("Error fetching data:", error);
          setErrors(error);
        }
      } finally {
        if (mounted) {
          setFetching(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [tid]);

  const renderSkillSets = (associateSkillSets, taskSkillSets) => {
    if (!associateSkillSets || associateSkillSets.length === 0) {
      return <span className="text-gray-400">-</span>;
    }

    const taskSkillIds = new Set();
    if (taskSkillSets && taskSkillSets.length > 0) {
      taskSkillSets.forEach((skill) => {
        const skillId = skill.id || skill.value || skill._id;
        if (skillId) taskSkillIds.add(skillId);
      });
    }

    const matchingSkills = [];
    const nonMatchingSkills = [];

    associateSkillSets.forEach((skill) => {
      const skillId = skill.id || skill.value || skill._id;
      const skillName = skill.name || skill.text || skill.label;
      const isMatching = taskSkillIds.has(skillId);

      if (isMatching) {
        matchingSkills.push({ id: skillId, name: skillName });
      } else {
        nonMatchingSkills.push({ id: skillId, name: skillName });
      }
    });

    return (
      <div className="flex flex-wrap gap-1">
        {matchingSkills.map((skill) => (
          <span
            key={skill.id}
            className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full"
            title="Matches job requirement"
          >
            ✓ {skill.name}
          </span>
        ))}
        {nonMatchingSkills.map((skill) => (
          <span
            key={skill.id}
            className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full"
          >
            {skill.name}
          </span>
        ))}
      </div>
    );
  };

  // Section Component - Using dark header pattern
  const DetailSection = ({ title, icon: Icon, children }) => (
    <div className="bg-gray-700 rounded-lg shadow-sm mb-4 sm:mb-6">
      <div className="px-4 sm:px-6 py-3 sm:py-4">
        <h3 className="text-base sm:text-lg font-semibold text-white flex items-center">
          <Icon className="w-4 sm:w-5 h-4 sm:h-5 mr-2 text-blue-300 flex-shrink-0" />
          <span className="truncate">{title}</span>
        </h3>
      </div>
      <div className="bg-white border-2 border-t-0 border-gray-700 rounded-b-lg p-4 sm:p-6">
        {children}
      </div>
    </div>
  );

  // Detail Field Component
  const DetailField = ({
    label,
    value,
    fullWidth = false,
    icon: Icon = null,
  }) => (
    <div className={fullWidth ? "lg:col-span-2" : ""}>
      <dt className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 flex items-center">
        {Icon && <Icon className="w-3 sm:w-4 h-3 sm:h-4 mr-1" />}
        {label}
      </dt>
      <dd className="text-base sm:text-lg font-medium text-gray-900 break-words">
        {value || "-"}
      </dd>
    </div>
  );

  if (forceURL !== "") {
    return <Navigate to={forceURL} />;
  }

  // Count away associates for display
  const awayAssociatesCount =
    associates?.results?.filter((a) => a.isAway).length || 0;
  const availableAssociatesCount =
    associates?.results?.filter((a) => !a.isAway).length || 0;
  const totalAssociatesCount = associates?.results?.length || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumbItems} />

        {/* Page Title - Responsive */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <UserGroupIcon className="w-6 sm:w-8 h-6 sm:h-8 mr-2 sm:mr-3 text-blue-600 flex-shrink-0" />
                Select Associate
              </h1>
              <p className="mt-1 text-xs sm:text-sm text-gray-600 flex items-center">
                <InformationCircleIcon className="w-3 sm:w-4 h-3 sm:h-4 mr-1 flex-shrink-0" />
                Choose an available associate for this task
              </p>
            </div>
          </div>
        </div>

        {/* Status Alerts - Responsive */}
        {task && (task.status === 2 || task.isClosed === true) && (
          <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg flex items-center text-sm sm:text-base">
            <InformationCircleIcon className="w-4 sm:w-5 h-4 sm:h-5 mr-2 flex-shrink-0" />
            This task is archived / closed
          </div>
        )}

        {/* Wizard Steps - Responsive */}
        <div className="mb-4 sm:mb-6 bg-white shadow-sm rounded-lg p-3 sm:p-4">
          {/* Mobile View */}
          <div className="md:hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full">
                  <span className="text-white font-semibold text-sm">2</span>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    Step 2: Select
                  </p>
                  <p className="text-xs text-gray-500">Choose associate</p>
                </div>
              </div>
              <div className="text-xs text-gray-500">2 of 4</div>
            </div>
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full"
                  style={{ width: "50%" }}
                ></div>
              </div>
            </div>
          </div>

          {/* Desktop View */}
          <div className="hidden md:flex items-center justify-center overflow-x-auto">
            <div className="flex items-center min-w-max">
              {/* Step 1 - Complete */}
              <div className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 bg-green-600 rounded-full">
                  <CheckCircleIcon className="w-6 h-6 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Review</p>
                  <p className="text-xs text-gray-500">Complete</p>
                </div>
              </div>

              {/* Connector */}
              <div className="mx-4 w-16 h-0.5 bg-green-600"></div>

              {/* Step 2 - Active */}
              <div className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-full">
                  <span className="text-white font-semibold">2</span>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Select</p>
                  <p className="text-xs text-gray-500">Associate</p>
                </div>
              </div>

              {/* Connector */}
              <div className="mx-4 w-16 h-0.5 bg-gray-300"></div>

              {/* Step 3 - Inactive */}
              <div className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 bg-gray-300 rounded-full">
                  <span className="text-gray-600 font-semibold">3</span>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Confirm</p>
                  <p className="text-xs text-gray-400">Assignment</p>
                </div>
              </div>

              {/* Connector */}
              <div className="mx-4 w-16 h-0.5 bg-gray-300"></div>

              {/* Step 4 - Inactive */}
              <div className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 bg-gray-300 rounded-full">
                  <span className="text-gray-600 font-semibold">4</span>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Complete</p>
                  <p className="text-xs text-gray-400">Finish</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message - Responsive */}
        {errors && Object.keys(errors).length > 0 && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base">
            <div className="flex justify-between items-center">
              <span className="flex items-center">
                <ExclamationCircleIcon className="w-4 sm:w-5 h-4 sm:h-5 mr-2 flex-shrink-0" />
                <strong>Error:</strong>
                {Object.entries(errors).map(([key, value]) => (
                  <span key={key} className="ml-2 break-words">
                    {key}: {value}
                  </span>
                ))}
              </span>
              <button
                onClick={() => setErrors({})}
                className="text-red-600 hover:text-red-800 ml-2 flex-shrink-0"
              >
                <XMarkIcon className="w-4 sm:w-5 h-4 sm:h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white shadow-sm rounded-lg">
          {isFetching ? (
            <div className="p-8 text-center">
              <Spinner size="lg" label="Loading..." />
            </div>
          ) : (
            <>
              {task && (
                <>
                  {/* Task Details Section using Dark Header Pattern */}
                  <DetailSection
                    title="Task Details"
                    icon={ClipboardDocumentListIcon}
                  >
                    <dl className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                      <DetailField label="Type" value="Assign Associate" />
                      <DetailField
                        label="Description"
                        value={task.description}
                      />
                      <DetailField
                        label="Job #"
                        value={
                          <Link
                            to={`/admin/order/${task.orderWjid}`}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            {task.orderWjid} →
                          </Link>
                        }
                      />
                      <DetailField
                        label="Job Start Date"
                        icon={CalendarDaysIcon}
                        value={
                          task.orderStartDate
                            ? new Date(task.orderStartDate).toLocaleDateString()
                            : "-"
                        }
                      />
                      <DetailField
                        label="Job Description"
                        value={task.orderDescription}
                        fullWidth
                      />
                      <div>
                        <dt className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                          Job Skill Sets
                        </dt>
                        <dd>
                          {task.orderSkillSets &&
                          task.orderSkillSets.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {task.orderSkillSets.map((skill, index) => (
                                <span
                                  key={skill.id || skill.value || index}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full"
                                >
                                  {skill.name || skill.text || skill.label}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                          Job Tags
                        </dt>
                        <dd>
                          {task.orderTags && task.orderTags.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {task.orderTags.map((tag, index) => (
                                <span
                                  key={tag.id || tag.value || index}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full"
                                >
                                  {tag.name || tag.text || tag.label}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </dd>
                      </div>
                      <DetailField
                        label="Client Name"
                        icon={UserIcon}
                        value={
                          <Link
                            to={`/admin/customer/${task.customerId}`}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            {task.customerName} →
                          </Link>
                        }
                      />
                      {task.customerPhone && (
                        <DetailField
                          label={`Client Phone (${CLIENT_PHONE_TYPE_OF_MAP[task.customerPhoneType]})`}
                          icon={PhoneIcon}
                          value={
                            <a
                              href={`tel:${task.customerPhone}`}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              {task.customerPhone}
                              {task.customerPhoneExtension && (
                                <span className="ml-1">
                                  ext. {task.customerPhoneExtension}
                                </span>
                              )}
                            </a>
                          }
                        />
                      )}
                      {task.customerFullAddressUrl && (
                        <DetailField
                          label="Client Address"
                          icon={MapPinIcon}
                          value={
                            <a
                              href={task.customerFullAddressUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700"
                            >
                              {task.customerFullAddressWithoutPostalCode}
                            </a>
                          }
                          fullWidth
                        />
                      )}
                      <div>
                        <dt className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 flex items-center">
                          <TagIcon className="w-3 sm:w-4 h-3 sm:h-4 mr-1" />
                          Client Tags
                        </dt>
                        <dd>
                          {task.customerTags && task.customerTags.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {task.customerTags.map((tag, index) => (
                                <span
                                  key={tag.id || tag.value || index}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full"
                                >
                                  {tag.name || tag.text || tag.label}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </dd>
                      </div>
                      <DetailField
                        label="Comments"
                        icon={ChatBubbleLeftRightIcon}
                        value={
                          <Link
                            to={`/admin/order/${task.orderWjid}/comments`}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            View comments →
                          </Link>
                        }
                      />
                    </dl>
                  </DetailSection>

                  {/* Available Associates Section */}
                  <DetailSection
                    title="Available Associates"
                    icon={UserGroupIcon}
                  >
                    {task.orderSkillSets && task.orderSkillSets.length > 0 && (
                      <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg flex items-center text-sm sm:text-base">
                        <InformationCircleIcon className="w-4 sm:w-5 h-4 sm:h-5 mr-2 flex-shrink-0" />
                        <strong>Note:</strong> Associates are filtered by
                        matching skill sets and sorted by least recent contacts.
                        Green highlighted skills match the job requirements.
                      </div>
                    )}

                    {awayAssociatesCount > 0 && (
                      <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg flex items-center text-sm sm:text-base">
                        <LockClosedIcon className="w-4 sm:w-5 h-4 sm:h-5 mr-2 flex-shrink-0" />
                        <div>
                          <strong>Restricted Associates:</strong>{" "}
                          {awayAssociatesCount} of {totalAssociatesCount}{" "}
                          associates are currently marked as away and cannot be
                          selected for assignment.
                          <br />
                          <span className="text-xs mt-1 inline-block">
                            Only {availableAssociatesCount} associate
                            {availableAssociatesCount !== 1 ? "s are" : " is"}{" "}
                            available for selection.
                          </span>
                        </div>
                      </div>
                    )}

                    {selectedAssociateId && (
                      <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg flex items-center text-sm sm:text-base">
                        <CheckCircleIcon className="w-4 sm:w-5 h-4 sm:h-5 mr-2 flex-shrink-0" />
                        <strong>Associate Selected:</strong> You have already
                        selected an associate. You can continue or choose a
                        different one.
                      </div>
                    )}

                    {associates &&
                    associates.results &&
                    associates.results.length > 0 ? (
                      <>
                        {/* Desktop View */}
                        <div className="hidden md:block overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  #
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Name
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Phone
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Email
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Contacts (30d)
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  WSIB #
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Rate
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Skills
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {associates.results.map((associate, index) => {
                                const isSelected =
                                  selectedAssociateId === associate.id;
                                const isAway = associate.isAway;

                                return (
                                  <tr
                                    key={associate.id}
                                    className={
                                      isSelected
                                        ? "bg-green-50"
                                        : isAway
                                          ? "bg-gray-100 opacity-60"
                                          : "hover:bg-gray-50"
                                    }
                                  >
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {index + 1}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      {isAway ? (
                                        <span
                                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                                          title="Associate is currently away and cannot be selected"
                                        >
                                          <LockClosedIcon className="w-3 h-3 mr-1" />
                                          Unavailable
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          <CheckCircleIcon className="w-3 h-3 mr-1" />
                                          Available
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      <Link
                                        to={`/admin/associate/${associate.id}`}
                                        target="_blank"
                                        className={`text-sm font-medium ${isAway ? "text-gray-500" : "text-blue-600 hover:text-blue-800"}`}
                                      >
                                        {associate.name}
                                      </Link>
                                      {isSelected && (
                                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                          Selected
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      {associate.phone ? (
                                        <a
                                          href={`tel:${associate.phone}`}
                                          className={`text-sm ${isAway ? "text-gray-500 pointer-events-none" : "text-blue-600 hover:text-blue-800"} flex items-center`}
                                        >
                                          <PhoneIcon className="w-4 h-4 mr-1" />
                                          {associate.phone}
                                        </a>
                                      ) : (
                                        <span className="text-sm text-gray-400">
                                          -
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      {associate.email ? (
                                        <a
                                          href={`mailto:${associate.email}`}
                                          className={`text-sm ${isAway ? "text-gray-500 pointer-events-none" : "text-blue-600 hover:text-blue-800"} flex items-center`}
                                        >
                                          <EnvelopeIcon className="w-4 h-4 mr-1" />
                                          {associate.email}
                                        </a>
                                      ) : (
                                        <span className="text-sm text-gray-400">
                                          -
                                        </span>
                                      )}
                                    </td>
                                    <td
                                      className={`px-4 py-4 whitespace-nowrap text-sm ${isAway ? "text-gray-500" : "text-gray-900"}`}
                                    >
                                      {associate.contactsLast30Days || 0}
                                    </td>
                                    <td
                                      className={`px-4 py-4 whitespace-nowrap text-sm ${isAway ? "text-gray-500" : "text-gray-900"}`}
                                    >
                                      {associate.wsibNumber || (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      {associate.hourlySalaryDesired ? (
                                        <span
                                          className={`text-sm font-medium ${isAway ? "text-gray-500" : "text-green-600"}`}
                                        >
                                          ${associate.hourlySalaryDesired}/hr
                                        </span>
                                      ) : (
                                        <span className="text-sm text-gray-400">
                                          -
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-4">
                                      <div
                                        className={isAway ? "opacity-50" : ""}
                                      >
                                        {renderSkillSets(
                                          associate.skillSets,
                                          task?.orderSkillSets,
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      {isAway ? (
                                        <div
                                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-200 rounded-md cursor-not-allowed"
                                          title="This associate is currently away and cannot be assigned"
                                        >
                                          <LockClosedIcon className="w-3 h-3 mr-1" />
                                          Restricted
                                        </div>
                                      ) : (
                                        <button
                                          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium text-white rounded-md ${
                                            isSelected
                                              ? "bg-green-600 hover:bg-green-700"
                                              : "bg-blue-600 hover:bg-blue-700"
                                          }`}
                                          onClick={() =>
                                            onSelectClick(associate)
                                          }
                                        >
                                          {isSelected ? "Reselect" : "Assign"}
                                          <ArrowRightIcon className="w-3 h-3 ml-1" />
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile View */}
                        <div className="md:hidden space-y-4">
                          {associates.results.map((associate, index) => {
                            const isSelected =
                              selectedAssociateId === associate.id;
                            const isAway = associate.isAway;

                            return (
                              <div
                                key={associate.id}
                                className={`p-4 rounded-lg border-2 ${
                                  isSelected
                                    ? "bg-green-50 border-green-200"
                                    : isAway
                                      ? "bg-gray-100 border-gray-300 opacity-75"
                                      : "bg-white border-gray-200"
                                }`}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <Link
                                      to={`/admin/associate/${associate.id}`}
                                      target="_blank"
                                      className={`text-sm font-medium ${isAway ? "text-gray-500" : "text-blue-600 hover:text-blue-800"}`}
                                    >
                                      {associate.name}
                                    </Link>
                                    <div className="flex items-center gap-2 mt-1">
                                      {isAway ? (
                                        <span
                                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                                          title="Associate is currently away and cannot be selected"
                                        >
                                          <LockClosedIcon className="w-3 h-3 mr-1" />
                                          Unavailable
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          <CheckCircleIcon className="w-3 h-3 mr-1" />
                                          Available
                                        </span>
                                      )}
                                      {isSelected && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                          Selected
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    #{index + 1}
                                  </span>
                                </div>

                                <div className="space-y-2 text-sm">
                                  {associate.phone && (
                                    <div className="flex items-center">
                                      <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" />
                                      <a
                                        href={`tel:${associate.phone}`}
                                        className={`${isAway ? "text-gray-500 pointer-events-none" : "text-blue-600 hover:text-blue-800"}`}
                                      >
                                        {associate.phone}
                                      </a>
                                    </div>
                                  )}

                                  {associate.email && (
                                    <div className="flex items-center">
                                      <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-400" />
                                      <a
                                        href={`mailto:${associate.email}`}
                                        className={`${isAway ? "text-gray-500 pointer-events-none" : "text-blue-600 hover:text-blue-800"} break-all`}
                                      >
                                        {associate.email}
                                      </a>
                                    </div>
                                  )}

                                  {associate.organizationName && (
                                    <div className="flex items-center">
                                      <BuildingOfficeIcon className="w-4 h-4 mr-2 text-gray-400" />
                                      <span
                                        className={
                                          isAway
                                            ? "text-gray-500"
                                            : "text-gray-900"
                                        }
                                      >
                                        {associate.organizationName}
                                      </span>
                                    </div>
                                  )}

                                  <div className="flex justify-between">
                                    <span className="text-gray-500">
                                      Contacts (30d):
                                    </span>
                                    <span
                                      className={
                                        isAway
                                          ? "text-gray-500"
                                          : "text-gray-900"
                                      }
                                    >
                                      {associate.contactsLast30Days || 0}
                                    </span>
                                  </div>

                                  <div className="flex justify-between">
                                    <span className="text-gray-500">
                                      WSIB #:
                                    </span>
                                    <span
                                      className={
                                        isAway
                                          ? "text-gray-500"
                                          : "text-gray-900"
                                      }
                                    >
                                      {associate.wsibNumber || "-"}
                                    </span>
                                  </div>

                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Rate:</span>
                                    <span
                                      className={`font-medium ${isAway ? "text-gray-500" : "text-green-600"}`}
                                    >
                                      {associate.hourlySalaryDesired
                                        ? `$${associate.hourlySalaryDesired}/hr`
                                        : "-"}
                                    </span>
                                  </div>

                                  <div>
                                    <span className="text-gray-500">
                                      Skills:
                                    </span>
                                    <div
                                      className={`mt-1 ${isAway ? "opacity-50" : ""}`}
                                    >
                                      {renderSkillSets(
                                        associate.skillSets,
                                        task?.orderSkillSets,
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {isAway ? (
                                  <div className="mt-4 w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-500 bg-gray-200 rounded-md cursor-not-allowed">
                                    <LockClosedIcon className="w-4 h-4 mr-2" />
                                    Cannot Assign - Associate is Away
                                  </div>
                                ) : (
                                  <button
                                    className={`mt-4 w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-md ${
                                      isSelected
                                        ? "bg-green-600 hover:bg-green-700"
                                        : "bg-blue-600 hover:bg-blue-700"
                                    }`}
                                    onClick={() => onSelectClick(associate)}
                                  >
                                    {isSelected ? "Reselect" : "Assign"}
                                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">
                          No Associates Available
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {task.orderSkillSets &&
                          task.orderSkillSets.length > 0 ? (
                            <>
                              No active associates found with the required skill
                              sets.{" "}
                              <Link
                                to="/admin/associates/add/step-1-search"
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Click here →
                              </Link>{" "}
                              to add a new associate.
                            </>
                          ) : (
                            <>
                              No active associates found.{" "}
                              <Link
                                to="/admin/associates/add/step-1-search"
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Click here →
                              </Link>{" "}
                              to add a new associate.
                            </>
                          )}
                        </p>
                      </div>
                    )}
                  </DetailSection>

                  {/* Navigation Buttons - Responsive */}
                  <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row justify-between gap-3">
                    <Link
                      to={`/admin/task/${tid}/assign-associate/step-1`}
                      className="order-2 sm:order-1"
                    >
                      <button className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-5 py-2 sm:py-2.5 text-sm sm:text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                        <ArrowLeftIcon className="w-4 h-4 mr-2" />
                        Back to Step 1
                      </button>
                    </Link>
                    {selectedAssociateId && (
                      <Link
                        to={`/admin/task/${tid}/assign-associate/step-3`}
                        className="order-1 sm:order-2"
                      >
                        <button className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-5 py-2 sm:py-2.5 text-sm sm:text-base font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                          Continue to Step 3
                          <ArrowRightIcon className="w-4 h-4 ml-2" />
                        </button>
                      </Link>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminTaskItemAssignAssociateStep2PageWithTheme() {
  return (
    <UIXThemeProvider>
      <AdminTaskItemAssignAssociateStep2Page />
    </UIXThemeProvider>
  );
}

export default AdminTaskItemAssignAssociateStep2PageWithTheme;
